import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { APP_CONFIG, AppConfig } from '../../../app.config';
import { StepUpAuthService } from '../services/step-up-auth.service';
import { getSignInRouteForUrl } from '../utils/route.utils';
import { getCorrelationId, isValidCorrelationId, resetCorrelationId } from '../utils/correlation-id.utils';

/**
 * HTTP interceptor that handles expired token responses.
 *
 * When the backend returns a 401 Unauthorized (token expired or invalid),
 * this interceptor:
 * 1. Clears all local auth state (tokens + auth level from sessionStorage).
 * 2. Redirects the user to the sign-in page of the current app (billing / documentupload / snl).
 *
 * This ensures users don't stay on protected pages with stale tokens
 * when the server rejects their requests.
 *
 * It also attaches the flow's correlation ID to auth-service and orchestration
 * calls (see withCorrelationId).
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const stepUpAuth = inject(StepUpAuthService);
  const config = inject(APP_CONFIG);

  return next(withCorrelationId(req, config)).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Skip redirect for login/auth endpoints — those handle their own 401s
        const isAuthEndpoint =
          req.url.includes('/auth/login') || req.url.includes('/auth/otp/verify');

        if (!isAuthEndpoint) {
          stepUpAuth.clearAuth();
          router.navigate([getSignInRouteForUrl(router.url)]);
        }
      }

      return throwError(() => error);
    })
  );
};

/**
 * Adds `correlationId` to auth-service and orchestration requests only — never to
 * third-party URLs such as S3 presigned uploads. Sent as a JSON body field
 * (query param for GET) rather than a header so no CORS/API Gateway change is
 * needed; the backends ignore unknown fields. A new ID starts on every /auth/login,
 * unless the login body already carries one (session link from Policy360), which
 * then becomes the ID for the rest of the flow.
 */
function withCorrelationId(req: HttpRequest<unknown>, config: AppConfig): HttpRequest<unknown> {
  const isOwnApi = [config.authApiUrl, config.portletOrchestrationURL].some(
    (baseUrl) => !!baseUrl && req.url.startsWith(baseUrl)
  );
  if (!isOwnApi) {
    return req;
  }

  let correlationId: string;
  if (req.url.includes('/auth/login')) {
    const incoming = (req.body as { correlationId?: unknown } | null)?.correlationId;
    correlationId = isValidCorrelationId(incoming) ? resetCorrelationId(incoming) : resetCorrelationId();
  } else {
    correlationId = getCorrelationId();
  }

  if (req.method === 'GET') {
    return req.clone({ setParams: { correlationId } });
  }
  // Only plain JSON objects — leave FormData, Blob, strings, etc. untouched
  if (Object.prototype.toString.call(req.body) === '[object Object]') {
    return req.clone({ body: { ...(req.body as object), correlationId } });
  }
  return req;
}
